import { computed, inject, Injectable, signal } from '@angular/core';

import { StorageService } from '../storage/storage.service';
import {
  DEFAULT_TENANT_PROFILE,
  FeatureFlag,
  ME_STORAGE_KEY,
  resolveTenantProfile,
  TenantProfile,
  TenantUser,
  VocabularyKey,
} from './instance-profile';

/**
 * TEC-294 (TEC-280, P1.6): el perfil de la instancia (`user.instance` de `me`) como signals.
 *
 * Se hidrata:
 * - al crearse, desde el `me` guardado (arranque con sesión);
 * - en cada actualización del usuario: `UserService.updateUser()` del app llama a `hydrate()`
 *   (login, login de conductor, registro, SAML, `getUserFromServer`, cambio de campus);
 * - y vuelve a los valores por omisión con `reset()` al cerrar sesión.
 *
 * Sin perfil (no hay sesión, o un API anterior a TEC-290) todo vale lo que el app tenía fijo:
 * producto universitario, vocabulario del Tec, el botón "Soy Estudiante/Colaborador" y las
 * banderas leídas de las llaves legadas de `campus_actual`.
 */
@Injectable({ providedIn: 'root' })
export class TenantConfig {
  readonly #storage = inject(StorageService);
  readonly #profile = signal<TenantProfile>(DEFAULT_TENANT_PROFILE);
  /** Ya llegó un usuario (o un logout) más nuevo que el `me` que se está leyendo al arrancar. */
  #settled = false;

  readonly product = computed(() => this.#profile().product);
  /** null = la marca empaquetada del app (ver `TenantProfile.brand`). */
  readonly brand = computed(() => this.#profile().brand);
  readonly vocabulary = computed(() => this.#profile().vocabulary);
  readonly sso = computed(() => this.#profile().sso);
  readonly features = computed(() => this.#profile().features);
  readonly daysToBook = computed(() => this.#profile().daysToBook);

  constructor() {
    void this.#restore();
  }

  /** ¿La bandera está encendida para el usuario y su campus actual? Reactivo: léelo en un `computed`. */
  feature(flag: FeatureFlag): boolean {
    return this.features()[flag];
  }

  /** Término del dominio según el producto: `term('student')` → "Estudiante" / "Colaborador". */
  term(key: VocabularyKey, plural = false): string {
    const term = this.vocabulary()[key];
    return plural ? term.plural : term.singular;
  }

  /** La palabra que concuerda con el término: `agree('scholarship', 'aplicada', 'aplicado')`. */
  agree(key: VocabularyKey, feminine: string, masculine: string): string {
    return this.vocabulary()[key].gender === 'f' ? feminine : masculine;
  }

  /** Aplica el usuario completo, ya mezclado (lo que queda guardado en `me`). */
  hydrate(user: TenantUser | null | undefined): void {
    this.#settled = true;
    this.#profile.set(resolveTenantProfile(user));
  }

  /** Logout: de vuelta a los valores por omisión. */
  reset(): void {
    this.#settled = true;
    this.#profile.set(DEFAULT_TENANT_PROFILE);
  }

  async #restore(): Promise<void> {
    try {
      const stored = await this.#storage.get<TenantUser>(ME_STORAGE_KEY);
      // Un login o un logout que ocurrió mientras se leía el almacenamiento manda sobre el `me` viejo.
      if (!this.#settled && stored) {
        this.#profile.set(resolveTenantProfile(stored));
      }
    } catch {
      // Sin almacenamiento legible se queda con los valores por omisión, igual que sin sesión.
    }
  }
}
